import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Form from '@atlaskit/form';
import { N200 } from '@atlaskit/theme/colors';
import { SitesType } from '@atlassiansox/asc-stargate';
import { isNotUndefined } from '@atlassiansox/bxp-shared-utils';
import {
  Environment,
  OfferingCatalog,
  ProductKey,
  SanitizedFormattedHtmlMessage,
  useCofsClient,
  useStargateClient
} from '@atlassiansox/bxpkit-core';
import styled from '@emotion/styled';
import React, { useEffect, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Route, Routes, useLocation } from 'react-router-dom';
import { useSignUpFormConfig } from '../../../../hook';
import { useSignUpFormGlobalConfiguration } from '../../../../hook/use-sign-up-form-global-configuration';
import {
  updateLoggedInFormDataSubmitStatus,
  useAppDispatch,
  useAppSelector
} from '../../../../redux';
import {
  getRedirectUrlFromShadowItError,
  isResponseError
} from '../../../../utility';
import { DevopsCarousel } from '../../component';
import { useDevopsState } from '../../hook';
import { messages } from '../../messages';
import { ProductPayloadActivateType, ProductPayloadType } from '../../types';
import {
  buildN2EPayload,
  buildSolutionStatsEvent,
  getSignupContext,
  pollForSuccess
} from '../../utility';
import {
  SlideFinalConfirmation,
  SlideSitenameExisting,
  SlideWorkspaceExisting
} from '../slide';
import { fontCharlieDisplay, fontCharlieText } from '../styled';

type FormProps = {
  readonly formProps: {
    readonly onSubmit: (e: React.FormEvent) => void;
    readonly ref: React.RefObject<HTMLFormElement>;
    readonly onKeyDown: (e: React.KeyboardEvent) => void;
  };
  readonly submitting: boolean;
};

function shouldSkipRecaptcha(email: string) {
  const botTestPattern =
    /(bottest@usersinbuckets.com|bottest@atl-paid-dev.com|bottest@bxpwpi.com)$/;
  return botTestPattern.test(email);
}

export function DevopsFormLoggedIn({
  gRecaptchaResponse
}: {
  gRecaptchaResponse: string;
}) {
  const [showNoCard, setShowNoCard] = useState(true);
  const devopsState = useDevopsState();
  const location = useLocation();
  const { locale, userInfo, bbworkspaces, enableSkipRecaptcha } = devopsState;
  const dispatch = useAppDispatch();
  const formData = useAppSelector(({ loggedInFormData }) => loggedInFormData);
  const cofsClient = useCofsClient();
  const stargateClient = useStargateClient();
  const { createAnalyticsEvent } = useAnalyticsEvents();
  const { firstName, lastName, email, sites, isNewUser } = userInfo || {};
  const { confirmationPageUrl } = useSignUpFormGlobalConfiguration();
  const { useCcpPlatform, platform } = useSignUpFormConfig();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const offeringCatalog = new OfferingCatalog(Environment.getEnvironment());

  const includeWorkspacesRoute =
    (userInfo?.sites || []).filter((site: SitesType) => site?.adminAccess)
      .length === 0 && bbworkspaces?.length !== 0;

  async function onSubmit() {
    dispatch(updateLoggedInFormDataSubmitStatus('submitting'));
    // TODO analytics for cloud provision/activate products submit
    if (!firstName || !lastName || !email) {
      console.error('Incomplete user info');
      // TODO: analytics, error message
      return;
    }
    createAnalyticsEvent({
      eventName: 'bxp.formCloudSignup.formSubmit',
      eventComponent: 'button',
      event: 'clicked',
      data: {
        type: 'N2E'
      }
    }).fire();
    const cloudId =
      sites?.find((site: SitesType) => site.displayName === formData.cloudName)
        ?.cloudId || '';
    const cloudName = formData?.cloudName || '';
    const signupContext = getSignupContext({ cloudName, createAnalyticsEvent });

    const payload = await buildN2EPayload({
      formData,
      locale,
      firstName,
      lastName,
      email,
      sites,
      bbworkspaces,
      createAnalyticsEvent
    }).catch((err) => {
      dispatch(updateLoggedInFormDataSubmitStatus('failure'));
      throw new Error(err);
    });
    const targetWorkspaceId =
      payload?.advancedSettings?.additionalProvisioningData?.workspaceUuid;
    const aaid = userInfo?.accountId;
    const solutionStatsEvent = buildSolutionStatsEvent({
      userInfo,
      createAnalyticsEvent,
      selectedSite:
        sites?.find(
          (site: SitesType) => site.displayName === formData.cloudName
        ) || undefined,
      provisionedProducts: (formData.productsNew || []).map((p) => p.product),
      targetWorkspaceId,
      aaid
    });

    const fireSolutionStatsEvent = (cloudId: string) => {
      if (solutionStatsEvent?.payload?.data?.isNewSiteProvisioned) {
        solutionStatsEvent.update({
          data: Object.assign({}, solutionStatsEvent?.payload?.data, {
            provisionedCloudId: cloudId,
            targetCloudId: cloudId
          })
        });
      }
      solutionStatsEvent && solutionStatsEvent.fire();
    };

    try {
      if (formData.cloudName) {
        // Check for Shadow IT authorization.
        await stargateClient.getActivationAuthorization({
          webPlatform: 'magnolia',
          productKeys: payload.products
            .filter(isNotUndefined)
            // @ts-ignore
            .map((item: ProductPayloadType | ProductPayloadActivateType) => {
              // Existing site payload has `productKey`, new site payload has `product`.
              if ('productKey' in item) {
                return item.productKey;
              }
              return item.product;
            }),
          productEdition: payload.products.filter(
            (product) => !!product && !!product.edition
          )[0].edition as string,
          cloudId,
          siteName: formData.cloudName
        });

        createAnalyticsEvent({
          event: 'authorized',
          eventName: 'devopsSignupShadowItAuthorized',
          eventType: 'track',
          eventComponent: 'devopsSignupShadowIt',
          eventColumn: 'devopsSignupShadowIt'
        }).fire();
      }
    } catch (error) {
      // Unauthorized due to Shadow IT.
      if (isResponseError(error) && error.response.status === 403) {
        createAnalyticsEvent({
          event: 'unauthorized',
          eventName: 'devopsSignupShadowItUnauthorized',
          eventType: 'track',
          eventComponent: 'devopsSignupShadowIt',
          eventColumn: 'devopsSignupShadowIt'
        }).fire();

        const redirectUrl = getRedirectUrlFromShadowItError(error);

        // Silently fail errors, unless explicitly unauthorized w/ redirectUrl.
        if (!!redirectUrl) {
          createAnalyticsEvent({
            event: 'redirect',
            eventName: 'devopsSignupShadowItRedirect',
            eventType: 'track',
            eventComponent: 'devopsSignupShadowIt',
            eventColumn: 'devopsSignupShadowIt'
          }).fire();
          // Unauthorized redirect to adminhub's Shadow IT request flow.
          window.location.assign(redirectUrl);
          return;
        }
      }
      // Silently fail unexpected errors for Shadow IT.
    }

    if (payload.products.length) {
      // turn all products into productKeys as existing site payload has `productKey`, new site payload has `product`.
      const productKeys = payload.products.map((item) => {
        if ('productKey' in item) {
          return {
            // @ts-ignore
            product: item.productKey,
            edition: item.edition
          };
        } else {
          return {
            product: item.product,
            edition: item.edition
          };
        }
      });

      const ccpProducts = productKeys
        .map(({ product, edition }) => {
          return offeringCatalog.mapProductKeyAndEditionToCcpProduct({
            productKey: product as ProductKey,
            edition: edition as string
          });
        })
        .filter((p) => p !== undefined);

      const skipRecaptcha =
        email && enableSkipRecaptcha && shouldSkipRecaptcha(email);

      if (!executeRecaptcha) {
        console.log('Execute recaptcha not yet available');
        return;
      }

      const token = skipRecaptcha
        ? 'reCaptcha'
        : await executeRecaptcha('userSignup');

      if (token !== null) {
        gRecaptchaResponse = token;
        // for existing sites use newly extracted cofsActivateProductsExisting endpoint, devops is only for cofs right now
        if (payload.activateProducts) {
          return stargateClient
            .cofsActivateProductExisting({
              cloudId: payload?.cloudId || '',
              // @ts-ignore
              products: payload.products,
              advancedSettings: payload.advancedSettings,
              signupContext: payload.signupContext,
              gRecaptchaResponse
            })
            .then(async (res) => {
              const progressUri = res.progressUri as string;

              await pollForSuccess({
                progressUri,
                confirmationPageUrl,
                pollProgress: () => cofsClient.pollProgress({ progressUri }),
                devopsState,
                dispatch,
                loggedInFormData: formData,
                onInitialRequestSuccess: fireSolutionStatsEvent,
                createAnalyticsEvent
              })
                .then((result) => {
                  if (result === 'COMPLETE_SUCCESS') {
                    dispatch(updateLoggedInFormDataSubmitStatus('success'));
                    return;
                  }
                  dispatch(updateLoggedInFormDataSubmitStatus('failure'));
                })
                .catch((reason) => {
                  createAnalyticsEvent({
                    eventName: 'bxp.formCloudSignup.progressUri',
                    eventComponent: 'button',
                    event: 'clicked',
                    data: {
                      err: reason
                    }
                  }).fire();
                  dispatch(updateLoggedInFormDataSubmitStatus('failure'));
                });
            })
            .catch((err) => {
              dispatch(updateLoggedInFormDataSubmitStatus('failure'));
              throw new Error(err);
            });
        }
        return stargateClient
          .activateProduct({
            email,
            displayName: firstName,
            ccpProducts,
            siteName: formData.cloudName,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            useCcpPlatform,
            consent: payload.consent,
            platform,
            advancedSettings: payload.advancedSettings,
            isNewUser,
            signupContext,
            gRecaptchaResponse: gRecaptchaResponse || ''
          })
          .then(async (res) => {
            // **NOTE**  With rollout integration, the response may be a COFS response, provisionRequestId may not exist, see below
            const requestId = res.metadata?.provisionRequestId;
            const products = payload.products
              ?.map(({ product }) => product)
              .join(',');

            if (res.service === 'ccp') {
              // Route user to CCP Confirmation page
              let redirectUrl = `${confirmationPageUrl}?platform=ccp&provisioningRequestId=${requestId}&ondemandurl=${formData.cloudName}&products=${products}`;

              if (res.cloudId) {
                redirectUrl += `&cloudId=${res.cloudId}`;
              }

              window.location.assign(redirectUrl);
            } else {
              const progressUri = res.progressUri as string;

              await pollForSuccess({
                progressUri,
                confirmationPageUrl,
                pollProgress: () => cofsClient.pollProgress({ progressUri }),
                devopsState,
                dispatch,
                loggedInFormData: formData,
                onInitialRequestSuccess: fireSolutionStatsEvent,
                createAnalyticsEvent
              })
                .then((result) => {
                  if (result === 'COMPLETE_SUCCESS') {
                    dispatch(updateLoggedInFormDataSubmitStatus('success'));
                    return;
                  }
                  dispatch(updateLoggedInFormDataSubmitStatus('failure'));
                })
                .catch((reason) => {
                  createAnalyticsEvent({
                    eventName: 'bxp.formCloudSignup.progressUri',
                    eventComponent: 'button',
                    event: 'clicked',
                    data: {
                      err: reason
                    }
                  }).fire();
                  dispatch(updateLoggedInFormDataSubmitStatus('failure'));
                });
            }
          });
      } else {
        throw new Error('Invalid Recaptcha Token');
      }
    }
  }

  useEffect(() => {
    if (formData.submitStatus === 'shouldSubmit') {
      void onSubmit();
    }
  }, [formData.submitStatus]);

  useEffect(() => {
    setShowNoCard(!(location?.pathname === '/confirmation') ?? true);
  }, [location]);

  const routes = [
    // Routes does not support undefined children, omit from array if not rendering
    ...[
      includeWorkspacesRoute && (
        <Route path="/workspace-picker" element={<SlideWorkspaceExisting />} />
      )
    ],
    <Route path="/site-picker" element={<SlideSitenameExisting />} />,
    <Route path="/confirmation" element={<SlideFinalConfirmation />} />
  ];

  return (
    <Form>
      {({ formProps }: FormProps) => (
        <StyledForm {...formProps}>
          <DevopsCarousel>
            <Routes children={routes} />
          </DevopsCarousel>
          {showNoCard && (
            <NoCard>
              <SanitizedFormattedHtmlMessage {...messages.noCard} />
            </NoCard>
          )}
        </StyledForm>
      )}
    </Form>
  );
}

const StyledForm = styled.form`
  font-family: ${fontCharlieText};

  label {
    color: ${N200};
    font-weight: 500;
    margin-bottom: 8px;

    span {
      display: none;
    }
  }
`;

const NoCard = styled.div`
  font-family: ${fontCharlieDisplay};
  font-weight: 500;
  font-size: 0.875rem;
  letter-spacing: 1px;
  line-height: 1.25;
  font-style: normal;
  margin-top: 46px;
  margin-bottom: 0;
  text-align: center;
  text-transform: uppercase;
`;
